import { catalogJsonUrl } from '../../js/config.js';
import { fetchCache } from '../../js/utils/fetchCache.js';
import { storage } from '../../js/utils/storage.js';
import { filterLocationsForSfw } from '../../js/utils/nsfwTags.js';
import { getBackgroundService } from '../services/BackgroundService.js';

const LOC_CACHE_KEY = 'background_loc_json';

export function parseSpriteState(spriteState) {
  if (spriteState === null || spriteState === undefined) {
    return { action: null };
  }

  const numericAction = parseInt(String(spriteState), 10);
  if (!isNaN(numericAction)) {
    return { action: numericAction };
  }

  return { action: null };
}

export const isValidUserSetLocation = (locId) => {
  if (locId == null || locId === '') return false;
  const value = String(locId);
  return value !== '0' && value !== '1' && !value.startsWith('ERROR');
};

export function extractBackgroundIdFromUrl(backgroundUrl) {
  if (!backgroundUrl) return '0';

  const match = backgroundUrl.match(/(\d+)\.\d+\.webp/) || backgroundUrl.match(/(\d+)/);
  return match ? match[1] : '0';
}

const storageImport = () => import('../../js/utils/storage.js').then(m => m.storage);

export async function getRecentConversationsFromStorage(characterId, limit = 20) {
  try {
    const storageModule = await storageImport();
    return storageModule.getRecentConversationsForCharacter(characterId, limit);
  } catch {
    return [];
  }
}

export function formatRelativeTime(date) {
  const diff = Math.floor((Date.now() - date) / 1000);

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

export function getCharacterColor(rank) {
  const colors = {
    1: '#ddcf0e',
    2: '#bdbba8',
    3: '#dd720e'
  };
  return colors[parseInt(rank, 10)] || 'var(--color-accent-purple)';
}

const locJsonToObject = (locations) => {
  if (Array.isArray(locations)) {
    return locations.reduce((acc, item) => {
      if (item && typeof item === 'object' && item.id != null && item.name) {
        acc[String(item.id)] = item.name;
      }
      return acc;
    }, {});
  }
  if (locations && typeof locations === 'object') {
    return Object.fromEntries(
      Object.entries(locations).map(([id, name]) => [
        String(id),
        typeof name === 'object' && name ? (name.name || String(name)) : name
      ])
    );
  }
  return {};
};

export const locObjectToArray = (locationsObject) =>
  Object.entries(locationsObject || {}).map(([id, name]) => ({ id: String(id), name }));

export async function fetchLocationsMap(lang = storage.getLanguage()) {
  const locations = await fetchCache.fetchJSON(catalogJsonUrl(`data/loc/${lang}.json`));
  const locationsObject = locJsonToObject(filterLocationsForSfw(locations));
  storage.set(LOC_CACHE_KEY, locationsObject);
  return locationsObject;
}

export function getCachedLocationsMap() {
  const cached = storage.get(LOC_CACHE_KEY);
  if (cached && typeof cached === 'object' && !Array.isArray(cached)) return cached;
  return null;
}

export async function getLocationNameById(locationId, fallback = `Location ${locationId}`) {
  const cached = getCachedLocationsMap();
  if (cached?.[String(locationId)]) return cached[String(locationId)];
  try {
    const locationsObject = await fetchLocationsMap();
    return locationsObject[String(locationId)] || fallback;
  } catch {
    return fallback;
  }
}

export async function getScenarioBackgroundImage(scenarioId, loc_id = null, timeOfDay = null) {
  const bgId = loc_id && loc_id !== '0' && loc_id !== 'undefined' && timeOfDay
    ? (parseInt(loc_id) > 0 ? loc_id : '31')
    : '31';
  const slot = timeOfDay || 'night';
  const backgroundService = getBackgroundService();
  try {
    return await backgroundService.resolveExistingBackgroundUrl(bgId, slot);
  } catch {
    return backgroundService.constructBackgroundUrl(bgId, slot);
  }
}

export function clearConversationCache() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('conversationCacheCleared', { detail: { timestamp: Date.now() } }));
  }
}
